import React, { useState, useMemo, useEffect } from 'react';
import { Hotel, TravellerInfo, Place } from '../../types/Quotation';
import AsyncSelect from 'react-select/async';
import { LocationOption } from '../../types/Location';
import { loadCityLocations } from '../../api/Location';
import { useHotels } from '../../hooks/useHotels';
import { fetchHotelPrices } from '../../api/Hotels';
import { summarizeQuotationRooms, toHotelSearchRooms, QuotationRoomOccupancy } from './quotationRoomOccupancy';
import RoomsTravellersDropdown from '../Common/RoomsTravellersDropdown';
import { resolveQuotationHotelCode } from './quotationHotelCode';
import '../../css/HotelListModal.css';

export type HotelSearchContext = {
  city: LocationOption;
  checkIn: string;
  checkOut: string;
};

interface HotelListModalProps {
  place: Place;
  checkIn: string;
  checkOut: string;
  travellers: TravellerInfo;
  onClose: () => void;
  onSelect: (hotel: Hotel) => void;
  initialHotels?: Hotel[];
  initialLoading?: boolean;
  initialError?: any;
  defaultMinStars?: number;
  mealTypeOverride?: string;
  rooms?: QuotationRoomOccupancy[];
  onRoomsChange?: (rooms: QuotationRoomOccupancy[]) => void;
  embedded?: boolean;
  onSelectionContext?: (context: HotelSearchContext) => void;
}

type HotelVariantOption = {
  id?: string;
  offerId?: string;
  name: string;
  price?: number;
  currency?: string;
  boardBasis?: string;
  refundable?: boolean;
  taxesIncluded?: boolean;
  raw?: any;
};

const parsePriceValue = (value: any): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.]/g, '');
    if (!cleaned) return undefined;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const extractPriceFromPriceInfo = (raw: any): number | undefined => {
  const minFinite = (nums: Array<number | undefined>) => {
    const finite = nums.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
    return finite.length ? Math.min(...finite) : undefined;
  };

  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const parsed = cleaned ? Number(cleaned) : NaN;
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (Array.isArray(raw)) {
    return minFinite(raw.map((v) => extractPriceFromPriceInfo(v)));
  }
  if (typeof raw === 'object') {
    const asAny = raw as any;
    // Common shape from prices API: { roomVariants: [{ price: ... }, ...] }
    if (Array.isArray(asAny.roomVariants) && asAny.roomVariants.length > 0) {
      const fromVariants = minFinite(asAny.roomVariants.map((v: any) => {
        // Try variant.price, but also common nested shapes
        return extractPriceFromPriceInfo(
          v?.price ?? v?.amount ?? v?.rate ?? v?.total ?? v?.totalAmount ?? v?.total_amount ?? v?.price?.amount ?? v?.price?.total
        );
      }));
      if (fromVariants !== undefined) return fromVariants;
    }
    if (Array.isArray(asAny.variants) && asAny.variants.length > 0) {
      const fromVariants = minFinite(asAny.variants.map((v: any) => extractPriceFromPriceInfo(v?.price ?? v?.amount ?? v?.total ?? v?.rate)));
      if (fromVariants !== undefined) return fromVariants;
    }
    if (Array.isArray(asAny.rooms) && asAny.rooms.length > 0) {
      const fromRooms = minFinite(asAny.rooms.map((v: any) => extractPriceFromPriceInfo(v?.price ?? v?.amount ?? v?.total ?? v?.rate)));
      if (fromRooms !== undefined) return fromRooms;
    }

    return extractPriceFromPriceInfo(
      asAny.price
        ?? asAny.amount
        ?? asAny.total
        ?? asAny.totalAmount
        ?? asAny.total_amount
        ?? asAny.net
        ?? asAny.netAmount
    );
  }
  return undefined;
};

const normalizeProvider = (hotel: any): string => {
  const value =
    hotel?.provider ||
    hotel?.source ||
    hotel?.supplier ||
    hotel?.provider_name ||
    hotel?.providerName ||
    hotel?.roomVariant?.provider ||
    '';
  return String(value || '').trim();
};

const isOtaPrimeHotel = (hotel: any): boolean => {
  const provider = normalizeProvider(hotel).toUpperCase().replace(/[^A-Z]/g, '');
  return provider.toUpperCase().includes('OTAPRIME');
};

const resolveHotelRef = (hotel: any): string => {
  const ref =
    hotel?.hotelRef ||
    hotel?.hotel_ref ||
    hotel?.ref ||
    hotel?.providerHotelId ||
    hotel?.provider_hotel_id ||
    hotel?.hotelId ||
    hotel?.hotel_id ||
    hotel?.id;
  if (ref === null || ref === undefined) return '';
  return String(ref).trim();
};

const resolveCityName = (hotel: any): string => {
  const value = hotel?.city_name || hotel?.cityName || hotel?.city || hotel?.location || '';
  return String(value || '').trim();
};

const resolveDisplayCurrency = (hotel: any): string => {
  const value = hotel?.display_currency || hotel?.displayCurrency || hotel?.currency || 'INR';
  return String(value || 'INR').trim().toUpperCase();
};

const resolveDisplayPrice = (hotel: any): number | undefined => {
  return (
    parsePriceValue(hotel?.display_price) ??
    parsePriceValue(hotel?.displayPrice) ??
    parsePriceValue(hotel?.price)
  );
};

const resolveHotelGalleryImages = (hotel: any): string[] => {
  const fromDirectImages = Array.isArray(hotel?.images) ? hotel.images : [];
  const fromDirectGallery = Array.isArray(hotel?.gallery) ? hotel.gallery : [];
  const fromRoomVariantImages = Array.isArray(hotel?.roomVariant?.images) ? hotel.roomVariant.images : [];
  const fromRoomVariantGallery = Array.isArray(hotel?.roomVariant?.gallery) ? hotel.roomVariant.gallery : [];
  const fromRoomVariants = Array.isArray(hotel?.roomVariants)
    ? hotel.roomVariants.flatMap((variant: any) => {
        const images = Array.isArray(variant?.images) ? variant.images : [];
        const gallery = Array.isArray(variant?.gallery) ? variant.gallery : [];
        return [...images, ...gallery];
      })
    : [];
  return Array.from(
    new Set(
      [
        ...fromDirectImages,
        ...fromDirectGallery,
        ...fromRoomVariantImages,
        ...fromRoomVariantGallery,
        ...fromRoomVariants,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );
};

const resolveHotelThumbnail = (hotel: any): string => {
  const direct =
    hotel?.thumbnail ||
    hotel?.image ||
    hotel?.imageUrl ||
    hotel?.image_url ||
    hotel?.thumbnailUrl ||
    hotel?.thumbnail_url ||
    hotel?.picture_url;
  if (direct) return String(direct);
  const gallery = resolveHotelGalleryImages(hotel);
  return gallery[0] || '';
};

const extractVariants = (h: any): HotelVariantOption[] => {
  // Prefer `roomVariants` as in TBO response; fallback to other common shapes
  const candidates: any[] =
    (Array.isArray(h?.roomVariants) && h.roomVariants) ||
    (Array.isArray(h?.variants) && h.variants) ||
    (Array.isArray(h?.rooms) && h.rooms) ||
    (Array.isArray(h?.roomTypes) && h.roomTypes) ||
    (Array.isArray(h?.room_types) && h.room_types) ||
    (Array.isArray(h?.availableRooms) && h.availableRooms) ||
    (Array.isArray(h?.ratePlans) && h.ratePlans) ||
    [];
  return candidates
    .map((v: any) => {
      const name = v?.name || v?.roomType || v?.title || v?.type || '';
      const price = parsePriceValue(v?.price)
        ?? parsePriceValue(v?.amount)
        ?? parsePriceValue(v?.rate)
        ?? parsePriceValue(v?.totalAmount)
        ?? parsePriceValue(v?.total_amount)
        ?? parsePriceValue(v?.total)
        ?? parsePriceValue(v?.price?.amount)
        ?? parsePriceValue(v?.price?.total)
        ?? parsePriceValue(v?.price?.totalAmount);
      const currency = v?.currency || h?.currency;
      const offerId = v?.offerId || v?.offer_id || undefined;
      const id = offerId || v?.id || v?.code || v?.plan_code || v?.ratePlanId || undefined;
      const boardBasis = v?.boardBasis;
      const refundable = typeof v?.refundable === 'boolean' ? v.refundable : undefined;
      const taxesIncluded = typeof v?.taxesIncluded === 'boolean' ? v.taxesIncluded : undefined;
      return name ? { id, offerId, name, price, currency, boardBasis, refundable, taxesIncluded, raw: v } : null;
    })
    .filter(Boolean) as HotelVariantOption[];
};

const getNumericPrice = (value: any): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parsed = cleaned ? Number(cleaned) : NaN;
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
  }
  return Number.POSITIVE_INFINITY;
};

const getHotelSortPrice = (hotel: any): number => {
  const variants = extractVariants(hotel);
  const variantPrices = variants.map((v) => getNumericPrice(v.price)).filter(Number.isFinite);
  if (variantPrices.length > 0) return Math.min(...variantPrices);
  return getNumericPrice(resolveDisplayPrice(hotel) ?? hotel?.price);
};

const HotelListModal: React.FC<HotelListModalProps> = ({
  place,
  checkIn: defaultCheckIn,
  checkOut: defaultCheckOut,
  travellers,
  onClose,
  onSelect,
  initialHotels = [],
  initialLoading = false,
  initialError = null,
  defaultMinStars = 0,
  mealTypeOverride,
  rooms,
  onRoomsChange,
  embedded = false,
  onSelectionContext,
}) => {
  const [city, setCity] = useState<LocationOption>({ label: place.label, value: place.value, type: 'city' });
  const [checkIn, setCheckIn] = useState(defaultCheckIn);
  const [checkOut, setCheckOut] = useState(defaultCheckOut);

  const [sortBy, setSortBy] = useState<'price' | 'rating'>('price');
  const [filterStar, setFilterStar] = useState<number>(() => Math.max(0, defaultMinStars));
  const [providerFilter, setProviderFilter] = useState<'OTAPRIME' | 'ALL'>('OTAPRIME');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [searchKey, setSearchKey] = useState<number>(0);
  const [hotelNameInput, setHotelNameInput] = useState<string>('');
  const [mealType, setMealType] = useState<string>(mealTypeOverride || ''); // keep as empty string to match expected payload

  useEffect(() => {
    setMealType(mealTypeOverride || '');
  }, [mealTypeOverride]);

  // Ensure we use a proper city code from Location API
  // Only run this when the modal first opens or when city label changes significantly
  useEffect(() => {
    const val = (city?.value || '').trim();
    const isAirport = /^[A-Za-z]{3}$/.test(val);
    const isCityId = /^\d+$/.test(val);
    if (isAirport || isCityId) return; // already a code/id
    const label = city?.label?.trim();
    if (!label || label.length < 3) return; // Don't search for very short labels
    
    // Only resolve city if we don't have a valid code and haven't searched yet
    if (searchKey === 0) {
      const timeoutId = setTimeout(() => {
        let cancelled = false;
        loadCityLocations(label).then(opts => {
          if (cancelled) return;
          const match = (opts || []).find(o => o.type === 'city' && o.label?.toLowerCase().includes(label.toLowerCase()));
          if (match && match.value !== city?.value) setCity(match);
        }).catch(() => void 0);
        return () => { cancelled = true; };
      }, 300); // Debounce for 300ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [city?.label, city?.value, searchKey]);

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    StarRating: filterStar, // send 0 as well
    MealType: mealType,
    HotelName: hotelNameInput.trim() || undefined,
    Provider: providerFilter === 'OTAPRIME' ? 'OTAPRIME' : undefined,
  }), [filterStar, mealType, hotelNameInput, providerFilter]);
  const roomsPayload = useMemo(() => (
    Array.isArray(rooms) && rooms.length > 0 ? toHotelSearchRooms(rooms) : []
  ), [rooms]);
  const roomSummary = useMemo(() => summarizeQuotationRooms(rooms || []), [rooms]);

  useEffect(() => {
    if (roomsPayload.length > 0 && searchKey === 0) {
      setSearchKey(1);
    }
  }, [rooms, roomsPayload.length, searchKey]);

  const hookResult = useHotels(
    (city?.value || '').trim(),
    checkIn,
    checkOut,
    travellers,
    page,
    limit,
    filters,
    searchKey,
    undefined,
    roomsPayload
  );

  const useLiveSearch = roomsPayload.length > 0;
  const hotels = !useLiveSearch && initialHotels.length ? initialHotels : hookResult.hotels;
  const loading = !useLiveSearch && initialHotels.length ? initialLoading : hookResult.loading;
  const error   = !useLiveSearch && initialHotels.length ? initialError   : hookResult.error;
  const pagination = hookResult.pagination;
  const priceJobId = !useLiveSearch && initialHotels.length ? undefined : hookResult.priceJobId;

  const [pricedHotels, setPricedHotels] = useState<any[]>([]);

  useEffect(() => {
    setPricedHotels(hotels as any[]);
  }, [hotels]);

  // Poll hotel prices using priceJobId (if provided by search API) and update displayed hotel list.
  useEffect(() => {
    if (!priceJobId) return;
    if (!Array.isArray(hotels) || hotels.length === 0) return;

    let cancelled = false;
    let timeoutId: any = null;
    let attempts = 0;

    const applyPricesToHotels = (prices: Record<string, any>) => {
      setPricedHotels((prev) => {
        const base = (prev && prev.length > 0) ? prev : (hotels as any[]);
        return base.map((h: any) => {
          const lookupKeys = [
            h?.hotelId,
            h?.hotel_id,
            h?.hotelRef,
            h?.hotel_ref,
            h?.ref,
            h?.providerHotelId,
            h?.provider_hotel_id,
            h?.id,
            h?.code,
          ]
            .map((value) => String(value ?? '').trim())
            .filter(Boolean);
          const matchedKey = lookupKeys.find((key) => Boolean(prices?.[key]));
          const priceInfo = matchedKey ? prices?.[matchedKey] : undefined;
          if (!priceInfo) {
            // OTAPrime prices come inline in roomVariants; do not mark those as sold out.
            if (isOtaPrimeHotel(h) || extractVariants(h).length > 0) {
              const next = { ...h };
              if ('soldOut' in next) delete next.soldOut;
              return next;
            }
            return { ...h, soldOut: true };
          }
          const next: any = { ...h };
          const nextPrice = extractPriceFromPriceInfo(priceInfo);
          if (typeof nextPrice === 'number' && Number.isFinite(nextPrice)) {
            next.price = nextPrice;
            next.display_price = nextPrice;
          }
          const nextCurrency =
            priceInfo?.currency ||
            priceInfo?.Currency ||
            priceInfo?.currencyCode ||
            priceInfo?.display_currency ||
            priceInfo?.displayCurrency ||
            priceInfo?.roomVariants?.[0]?.currency ||
            priceInfo?.variants?.[0]?.currency ||
            priceInfo?.rooms?.[0]?.currency;
          if (typeof nextCurrency === 'string' && nextCurrency.trim()) {
            next.currency = nextCurrency;
            next.display_currency = nextCurrency;
          }
          // If API returns variants, keep them for downstream pricing/selection
          if (Array.isArray(priceInfo?.roomVariants)) next.roomVariants = priceInfo.roomVariants;
          if (Array.isArray(priceInfo?.variants)) next.variants = priceInfo.variants;
          if (Array.isArray(priceInfo?.rooms)) next.rooms = priceInfo.rooms;
          // Remove soldOut marker when present in returned price list
          if ('soldOut' in next) delete next.soldOut;
          return next;
        });
      });
    };

    const poll = async () => {
      attempts += 1;
      try {
        const resp = await fetchHotelPrices(priceJobId);
        if (cancelled) return;

        if (resp?.prices && typeof resp.prices === 'object') {
          applyPricesToHotels(resp.prices);
        }

        // Keep polling while status is PENDING (or until we get DONE).
        if (resp?.status === 'DONE') return;
      } catch {
        // Swallow and keep polling a few times; backend may not have the job ready immediately.
      }

      if (cancelled) return;
      if (attempts >= 30) return; // ~45s at 1.5s intervals
      timeoutId = setTimeout(poll, 1500);
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [priceJobId, searchKey, hotels]);

  // Track room type per visible hotel card (by index in current list)
  const [roomTypes, setRoomTypes] = useState<Record<string, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<Record<string, any>>({});

  const getHotelKey = (hotel: any, index: number) => (
    String(
      hotel?.hotelId ||
      hotel?.hotel_id ||
      hotel?.hotelRef ||
      hotel?.hotel_ref ||
      hotel?.providerHotelId ||
      hotel?.provider_hotel_id ||
      hotel?.id ||
      hotel?.name ||
      index
    )
  );

  // Helper: decide if a variant includes breakfast via board basis or inclusions
  const hasBreakfast = (v: any): boolean => {
    const basisRaw = v?.boardBasis || v?.board_basis || '';
    const basis = typeof basisRaw === 'string' ? basisRaw.toLowerCase() : '';
    const incs: string[] = Array.isArray(v?.inclusions) ? v.inclusions : [];
    const incsText = incs.join(' ').toLowerCase();
    // Common codes: CP = Continental Plan (breakfast), MAP/AP include breakfast, half/full board include breakfast
    const basisHas = ['breakfast', 'with_breakfast', 'bed_and_breakfast', 'bed & breakfast', 'cp', 'map', 'ap', 'half_board', 'full_board', 'all_inclusive']
      .some(key => basis.includes(key));
    const inclusionsHas = /break\s*fast|breakfast|complimentary\s*breakfast|free\s*breakfast/i.test(incsText);
    return basisHas || inclusionsHas;
  };

  // Pick default variant: lowest price that includes breakfast; else absolute lowest price
  const pickDefaultVariant = (variants: any[]): any | undefined => {
    if (!variants || variants.length === 0) return undefined;
    const numericPrice = (v: any) => (typeof v?.price === 'number' ? v.price : Number.POSITIVE_INFINITY);
    const withBreakfast = variants.filter(hasBreakfast);
    const pool = withBreakfast.length ? withBreakfast : variants;
    return pool.slice().sort((a, b) => numericPrice(a) - numericPrice(b))[0];
  };

  // Auto-select default variant per hotel when results change (do not override user choice)
  useEffect(() => {
    try {
      if (!Array.isArray(pricedHotels) || pricedHotels.length === 0) return;
      const nextSel: Record<string, any> = { ...selectedVariant };
      const nextTypes: Record<string, string> = { ...roomTypes };
      let changed = false;
      pricedHotels.forEach((h, i) => {
        const key = getHotelKey(h, i);
        if (nextSel[key]) return; // respect user selection
        const vars = extractVariants(h);
        if (!vars || vars.length === 0) return;
        const def = pickDefaultVariant(vars);
        if (def) {
          nextSel[key] = def;
          nextTypes[key] = (def.id ?? def.name ?? '') as string;
          changed = true;
        }
      });
      if (changed) {
        setSelectedVariant(nextSel);
        setRoomTypes(nextTypes);
      }
    } catch {}
    // Only react to list changes; do not include selectedVariant/roomTypes to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricedHotels]);

  // No children age inputs in modal; API will receive empty list when not provided

  const visibleHotels = useMemo(() => {
    let list = pricedHotels.length > 0 ? pricedHotels : (hotels as any[]);
    if (providerFilter === 'OTAPRIME') {
      list = list.filter(h => isOtaPrimeHotel(h));
    }
    if (filterStar > 0) {
      list = list.filter(h => Number(h.starRating) === filterStar);
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'price') {
        return getHotelSortPrice(a) - getHotelSortPrice(b);
      } else {
        return Number(b.starRating) - Number(a.starRating);
      }
    });
  }, [hotels, pricedHotels, sortBy, filterStar, providerFilter]);

  // Removed debouncing — search triggers only via button click

  const content = (
      <div className="modal hotel-modal" role="dialog" aria-modal={!embedded} aria-label="Select hotel">
        {!embedded && <button className="close-btn" onClick={onClose}>×</button>}

        <div className="hotel-search-form">
          <div className="hs-field">
            <label>City</label>
            <AsyncSelect<LocationOption, false>
              cacheOptions
              defaultOptions
              loadOptions={loadCityLocations}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              value={city}
              onChange={opt => setCity(opt as LocationOption)}
              placeholder="Type or select a city"
              classNamePrefix="rs"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                menu: (base) => ({ ...base, zIndex: 2000 }),
                control: (base) => ({ ...base, minHeight: 36, borderColor: '#ccc', boxShadow: 'none' }),
                valueContainer: (base) => ({ ...base, padding: '0 8px' }),
              }}
            />
          </div>
          <div className="hs-field">
            <label>Check‑in</label>
            <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)} />
          </div>
          <div className="hs-field">
            <label>Check‑out</label>
            <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)} />
          </div>
          {embedded && rooms && onRoomsChange && (
            <div className="hs-field hs-field--rooms">
              <label>Rooms & Travellers</label>
              <RoomsTravellersDropdown value={rooms} onChange={onRoomsChange} />
            </div>
          )}
          {/* Children age inputs removed per requirements */}
          <button
            className="hs-search-btn"
            onClick={() => {
              setPage(1);
              setSearchKey(k => k + 1);
            }}
          >
            Search
          </button>
        </div>
        {roomsPayload.length > 0 && (
          <div className="hotel-controls">
            <div className="rfq-muted">Rooms selected: {roomSummary}</div>
          </div>
        )}

        <div className="hotel-controls">
          <div className="hc-sort">
            <span>Sort by:</span>
            <button
              className={sortBy === 'price' ? 'active' : ''}
              onClick={() => setSortBy('price')}
            >
              Price
            </button>
            <button
              className={sortBy === 'rating' ? 'active' : ''}
              onClick={() => setSortBy('rating')}
            >
              Rating
            </button>
          </div>
          <div className="hc-filter hc-provider-filter">
            <span className="hc-filter-label">Provider:</span>
            <label className="hc-radio-option">
              <input
                type="radio"
                name="provider-filter"
                value="OTAPRIME"
                checked={providerFilter === 'OTAPRIME'}
                onChange={() => setProviderFilter('OTAPRIME')}
              />
              OTAPRIME only
            </label>
            <label className="hc-radio-option">
              <input
                type="radio"
                name="provider-filter"
                value="ALL"
                checked={providerFilter === 'ALL'}
                onChange={() => setProviderFilter('ALL')}
              />
              All providers
            </label>
          </div>
          <div className="hc-filter">
            <label>Name:</label>
            <input
              type="text"
              value={hotelNameInput}
              onChange={e => setHotelNameInput(e.target.value)}
              placeholder="Search by hotel name"
            />
          </div>
          <div className="hc-filter">
            <label>Star Rating:</label>
            <select
              value={filterStar}
              onChange={e => {
                setFilterStar(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={0}>All</option>
              {[5,4,3,2,1].map(s => (
                <option key={s} value={s}>{s}★</option>
              ))}
            </select>
          </div>
          <div className="hc-filter">
            <label>Page size:</label>
            <select
              value={limit}
              onChange={e => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10,20,30,50,100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="hotel-list">
          {loading && <p className="loading">Loading hotels…</p>}
          {error && <p className="error">{error.message}</p>}
          {!loading && !error && visibleHotels.map((h, i) => (
            <div className="hotel-card" key={i}>
              {(() => {
                const provider = normalizeProvider(h);
                const hotelRef = resolveHotelRef(h);
                const cityName = resolveCityName(h);
                const thumbnail = resolveHotelThumbnail(h);
                const galleryImages = resolveHotelGalleryImages(h);
                const displayCurrency = resolveDisplayCurrency(h);
                const displayPrice = resolveDisplayPrice(h);
                const displayCurrencySymbol =
                  displayCurrency === 'USD'
                    ? '$'
                    : displayCurrency === 'EUR'
                      ? '€'
                      : displayCurrency === 'INR'
                        ? '₹'
                        : '';
                const displayPriceText =
                  typeof displayPrice === 'number' && Number.isFinite(displayPrice)
                    ? `${displayPrice.toLocaleString('en-IN')}`
                    : '-';
                return (
              <div className="hotel-main">
                <img
                  src={thumbnail || '/placeholder-hotel.png'}
                  alt={h.name}
                  className="hotel-thumbnail"
                />
                <div className="hotel-info">
                  <h3 className="hotel-name">{h.name}</h3>
                  <div className="hotel-address">
                    Provider: {provider || '-'} | Hotel Ref: {hotelRef || '-'} | City: {cityName || '-'}
                  </div>
                  <div className="hotel-stars">
                    {Array.from({ length: Number(h.starRating) })
                      .map(() => '★')
                      .join('')}
                  </div>
                  {h.address && (
                    <div className="hotel-address" title={h.address}>{h.address}</div>
                  )}
                  <div className="hotel-address">
                    Display Price: {displayCurrency || 'INR'} {displayPriceText === '-' ? '-' : `${displayCurrencySymbol}${displayPriceText}`}
                  </div>
                  <div className="hotel-amenities">
                    {(h.amenities ?? []).slice(0, 3).map((amenity: string, idx: number) => (
                      <span className="amenity-badge" key={idx}>
                        {amenity}
                      </span>
                    ))}
                    {(h.amenities ?? []).length > 3 && (
                      <span className="amenity-more">
                        +{h.amenities.length - 3} more
                      </span>
                    )}
                  </div>
                  {galleryImages.length > 0 && (
                    <div className="hotel-amenities">
                      {galleryImages.slice(0, 3).map((imageUrl, idx) => (
                        <img
                          key={`${imageUrl}-${idx}`}
                          src={imageUrl}
                          alt={`${h.name} ${idx + 1}`}
                          style={{ width: 36, height: 24, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ))}
                      {galleryImages.length > 3 && (
                        <span className="amenity-more">+{galleryImages.length - 3} images</span>
                      )}
                    </div>
                  )}
                </div>
                {(() => {
                  const variants = extractVariants(h);
                  const hotelKey = getHotelKey(h, i);
                  const selected = selectedVariant[hotelKey];
                  const selectedPrice = getNumericPrice(selected?.price ?? resolveDisplayPrice(h) ?? h.price);
                  const priceValue = Number.isFinite(selectedPrice) ? selectedPrice : 0;
                  const currency = (selected?.currency || h.currency || 'INR') as string;
                  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';
                  return (
                    <div className="hotel-rate" style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                      <span className="hotel-price">{symbol}{Number(priceValue || 0).toLocaleString('en-IN')}</span>
                      <select
                        value={roomTypes[hotelKey] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRoomTypes(prev => ({ ...prev, [hotelKey]: value }));
                          const next = variants.find(v => (v.id ?? v.name) === value);
                          setSelectedVariant(prev => ({ ...prev, [hotelKey]: next }));
                        }}
                        title="Select room type"
                        style={{ minWidth: 140 }}
                      >
                        <option value="">Select room type</option>
                        {variants.map((v, idx) => {
                          const labelCurrency = v.currency || currency;
                          const labelSymbol = labelCurrency === 'USD' ? '$' : labelCurrency === 'EUR' ? '€' : '₹';
                          // Normalize board basis text
                          const basis = typeof v.boardBasis === 'string' ? v.boardBasis.replace(/_/g, ' ') : undefined;
                          const parts = [v.name, basis].filter(Boolean).join(' — ');
                          const pricePart = typeof v.price === 'number' ? ` — ${labelSymbol}${v.price.toLocaleString('en-IN')}` : '';
                          return (
                            <option key={v.id ?? idx} value={v.id ?? v.name}>
                              {parts}{pricePart}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })()}
              </div>
                );
              })()}
              <div className="hotel-action" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="select-btn"
                  onClick={() => {
                    const hotelKey = getHotelKey(h, i);
                    const sv = selectedVariant[hotelKey];
                    const chosenType = (sv?.name || roomTypes[hotelKey] || '').trim() || undefined;
                    const chosenPrice = getNumericPrice(sv?.price ?? resolveDisplayPrice(h) ?? h.price);
                    const chosenId = (sv?.offerId || sv?.id) as string | undefined;
                    onSelect({
                      ...h,
                      hotelCode: resolveQuotationHotelCode(h),
                      price: Number.isFinite(chosenPrice) ? chosenPrice : 0,
                      roomType: chosenType,
                      roomVariantId: chosenId,
                      roomVariant: sv,
                    });
                    onSelectionContext?.({ city, checkIn, checkOut });
                  }}
                  title="Select hotel with chosen room type"
                >
                  Select
                </button>
              </div>
            </div>
          ))}
        </div>
        {!loading && !error && pagination && (
          <div className="hotel-pagination">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="page-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} results)
            </span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
  );

  return embedded ? <div className="hotelListEmbedded">{content}</div> : <div className="modal-backdrop">{content}</div>;
};

export default HotelListModal;
